import { ComboBox, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DEFAULT_PRODUCT } from '@features/products/constants/defaultProduct'
import { ProductAction } from '@features/products/enums/productAction'
import { useFetchProducts } from '@features/products/hooks/useFetchProducts'
import { Product } from '@features/products/types/product'
import { ProductsFilters } from '@features/products/types/productsFilters'

import { ModalId } from '../../../../../../../../../../../../../../../../../../enums/modalId'
import { useDebouncedCallback } from '../../../../../../../../../../../../../../../../../../hooks/useDebouncedCallback'
import { useDebouncedValue } from '../../../../../../../../../../../../../../../../../../hooks/useDebouncedValue'
import { ProductEditor } from '../../../../../../../../../../../../../../../../../products/elements/ProductEditor'
import { useInvoiceEditorForm } from '../../../../../../../../../../../../../../contexts/invoiceEditorFormContext'
import { useUpdateDependentLineValues } from '../../../../../../../../../../hooks/useUpdateDependentLineValues'
import { CommonInvoiceLineFieldProps } from '../../../../../../types/commonInvoiceLineFieldProps'
import { ProductNameComboBoxDropdownFooter } from './elements/ProductNameComboBoxDropdownFooter'
import { getItems } from './utils/getItems'

const PRODUCT_SEARCH_DEBOUNCE_MS = 300
const PRODUCT_SEARCH_PAGE_SIZE = 50

type InvoiceLineProductNameProps = CommonInvoiceLineFieldProps

export const InvoiceLineProductName = ({ disabled = false, lineIndex }: InvoiceLineProductNameProps): ReactElement => {
  const [createdProduct, setCreatedProduct] = useState<Product>()
  const [productSearchQuery, setProductSearchQuery] = useState('')
  const { t } = useTranslation()

  const { invoiceEditorForm, trackInvoiceEditor } = useInvoiceEditorForm()
  const { FormItem, setValue, watch } = invoiceEditorForm
  const { handleProductChange } = useUpdateDependentLineValues()
  const debouncedProductSearchQuery = useDebouncedValue(productSearchQuery, PRODUCT_SEARCH_DEBOUNCE_MS)
  const productsFilters = useMemo<ProductsFilters>(
    () => ({ pageSize: PRODUCT_SEARCH_PAGE_SIZE, searchQuery: debouncedProductSearchQuery.trim() }),
    [debouncedProductSearchQuery],
  )
  const { products } = useFetchProducts(productsFilters, { keepPreviousData: true })
  const [productName, productFromSearch] = watch([
    `invoiceLines.${lineIndex}.productName`,
    `invoiceLines.${lineIndex}.productFromSearch`,
  ])
  const { close } = useModal(ModalId.ProductEditorModal)

  // The ComboBox also matches items by their value (the id), so only name matches may reach it.
  const productsMatchingName = useMemo(
    () => products.filter(({ name }) => name.toLowerCase().includes(productSearchQuery.toLowerCase())),
    [productSearchQuery, products],
  )

  const items = useMemo(() => getItems(productsMatchingName), [productsMatchingName])

  const trackDebouncedInputTyping = useDebouncedCallback((value: string) =>
    trackInvoiceEditor('Form - Invoice lines - Item name input typed', {
      textLength: value.length,
    }),
  )

  const handleProductNameInput = useCallback(
    (name: string) => {
      setValue(`invoiceLines.${lineIndex}.productName`, name)
      setValue(`invoiceLines.${lineIndex}.productFromSearch`, null)
      setValue(`invoiceLines.${lineIndex}.productId`, null)
      trackDebouncedInputTyping(name)
    },
    [lineIndex, setValue, trackDebouncedInputTyping],
  )

  const handleProductSearchInput = useCallback(
    (name: string) => {
      setProductSearchQuery(name)
      handleProductNameInput(name)
    },
    [handleProductNameInput],
  )

  const handleProductSelect = useCallback(
    (onChange: (product: Product) => void) => (productId: string) => {
      const product = productsMatchingName.find((product) => product.id === productId)

      if (product) {
        handleProductNameInput(product.name)
        setProductSearchQuery('')
        handleProductChange(lineIndex, product)
        onChange(product)

        trackInvoiceEditor('Form - Invoice lines - Item selected from the list', {
          productId: product.id,
        })
      }
    },
    [lineIndex, handleProductNameInput, handleProductChange, productsMatchingName, trackInvoiceEditor],
  )

  const handleProductEditorClose = useCallback(() => {
    close()
  }, [close])

  const handleCreateProductSuccess = useCallback(
    (createdProduct: Product) => {
      handleProductChange(lineIndex, createdProduct)
      setValue(`invoiceLines.${lineIndex}.productName`, createdProduct.name)
      setValue(`invoiceLines.${lineIndex}.productFromSearch`, createdProduct)
      setProductSearchQuery('')
      setCreatedProduct(createdProduct)
    },

    [handleProductChange, lineIndex, setValue],
  )

  const productEditorInitialProduct = useMemo<Product>(
    () => ({
      ...DEFAULT_PRODUCT,
      name: productName !== productFromSearch?.name ? productName : '',
    }),
    [productFromSearch, productName],
  )

  return (
    <FormItem
      name={`invoiceLines.${lineIndex}.productFromSearch`}
      render={({ field: { value, onChange, ...rest } }) => {
        return (
          <>
            <ComboBox
              {...rest}
              aria-label={t('external_invoices.editor.form.field.line.header.name')}
              key={createdProduct?.id}
              addNewItemButtonText={t('external_invoices.editor.form.field.line.field.product_name.add_one_off')}
              disabled={disabled}
              footer={ProductNameComboBoxDropdownFooter}
              initialInputValue={productName || value?.name || ''}
              isMultiselect={false}
              items={items}
              onChange={handleProductSelect(onChange)}
              onInputChange={(event) => handleProductSearchInput(event.target.value)}
              placeholder={t('external_invoices.editor.form.field.line.field.product_name.search_or_type')}
              selectedId={value?.id}
              shouldDisplayItemValue={false}
              size="sm"
            />
            <ProductEditor
              action={ProductAction.Create}
              onClose={handleProductEditorClose}
              onCreateProductSuccess={handleCreateProductSuccess}
              product={productEditorInitialProduct}
              id={ModalId.ProductEditorModal}
              view="invoiceEditor"
            />
          </>
        )
      }}
    />
  )
}
